/**
 * "Buy first" (just-in-time blanks on the batch pick list): the plain helpers
 * the card, the print section, the batch screen and the batch-created toast
 * share. Components live in `components/BuyFirstSection.tsx`.
 */
import type { JitBuyList } from '@threadheads/shared';

/** The anchor the createBatch toast's "Open batch" lands on. */
export const BUY_FIRST_ANCHOR = 'buy-first';

/**
 * The `.buy-first` rule for the pick-list print's own stylesheet: the Buy
 * first section always starts a new sheet (the element carries the same rule
 * inline, for engines that read only one of the two).
 */
export const BUY_FIRST_PRINT_CSS = `
  .buy-first { break-before: page; page-break-before: always; }
  .buy-first h3 { break-after: avoid; page-break-after: avoid; }
`;

/** Something to buy: a PO to buy on, or units still waiting for one. */
export const hasBlanksToBuy = (buyList: JitBuyList): boolean =>
  buyList.purchaseOrders.length > 0 || buyList.unlinkedUnits > 0;

/** BLU-1506: blanks this batch takes from an arrived Order and wait box. */
export const fromBoxesOf = (buyList: JitBuyList): NonNullable<JitBuyList['fromBoxes']> => buyList.fromBoxes ?? [];

/** Anything to say: something to buy, or blanks to take from an arrived box. */
export const hasBuyFirstContent = (buyList: JitBuyList | null | undefined): buyList is JitBuyList =>
  !!buyList && (hasBlanksToBuy(buyList) || fromBoxesOf(buyList).length > 0);

const sumQty = (groups: ReadonlyArray<{ lines: ReadonlyArray<{ qty: number }> }>): number =>
  groups.reduce((sum, group) => sum + group.lines.reduce((lineSum, line) => lineSum + line.qty, 0), 0);

/** Blanks to buy: every line's quantity, across every PO. */
export const buyListBlankCount = (buyList: JitBuyList): number => sumQty(buyList.purchaseOrders);

/** Blanks to take from arrived boxes, across every box. */
export const fromBoxesBlankCount = (buyList: JitBuyList): number => sumQty(fromBoxesOf(buyList));
